import { Stack, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { formatCurrency } from "web_myplann/Infrastructure/Utils/CurrencyUtils";
import { GoalRecapRow } from "web_myplann/Inspiration/Components/GoalRecapRow";
import { InspirationSheet } from "web_myplann/Inspiration/Components/InspirationSheet";
import { INSPIRATION_CURRENCY } from "web_myplann/Inspiration/constants";
import type { MortgageRateBand } from "web_myplann/Inspiration/Types/MortgageRateBand";
import type { OwnHomeGoalPreset } from "web_myplann/Inspiration/Utils/ownHomeMath";
import { formatRate } from "web_myplann/Shared/Utils/percentageFormatters";

const R = Resources.Inspiration.OwnHome.Result.createGoalSheet;

type Props = {
	open: boolean;
	preset: OwnHomeGoalPreset;
	rates: MortgageRateBand;
	onClose: () => void;
	onContinue: () => void;
};

export const OwnHomeCreateGoalSheet: FC<Props> = ({
	open,
	preset,
	rates,
	onClose,
	onContinue,
}) => {
	const { t } = useResource();
	const theme = useTheme();

	return (
		<InspirationSheet
			open={open}
			onClose={onClose}
			title={t(R.title)}
			description={t(R.description)}
			primaryLabel={t(R.continue)}
			onPrimary={onContinue}
			secondaryLabel={t(Resources.Common.Cancel)}
			onSecondary={onClose}
		>
			<Stack
				bgcolor={theme.palette.surface.muted}
				borderRadius={theme.radii.lg}
				p={1.2}
				gap={1.2}
			>
				<GoalRecapRow
					label={t(R.priceLabel)}
					note={t(R.priceNote, { rate: formatRate(rates.best) })}
					value={formatCurrency(preset.totalValue, INSPIRATION_CURRENCY, 0)}
				/>
				<GoalRecapRow
					label={t(R.loanLabel)}
					note={t(R.loanNote)}
					value={formatCurrency(preset.loanAmount, INSPIRATION_CURRENCY, 0)}
				/>
				{preset.ownFunds > 0 && (
					<GoalRecapRow
						label={t(R.savingsLabel)}
						note={t(R.savingsNote)}
						value={formatCurrency(preset.ownFunds, INSPIRATION_CURRENCY, 0)}
					/>
				)}
				<Stack
					pt={1.2}
					borderTop={theme.colors.border.solid(theme.palette.grey[200])}
				>
					<GoalRecapRow
						label={t(R.targetLabel)}
						note={t(R.targetNote)}
						value={formatCurrency(
							preset.savingsTarget,
							INSPIRATION_CURRENCY,
							0,
						)}
						highlight
					/>
				</Stack>
			</Stack>

			<Typography
				variant="hint"
				color={theme.palette.grey[500]}
				lineHeight={1.625}
			>
				{t(R.footnote)}
			</Typography>
		</InspirationSheet>
	);
};
